/**
 * Piezas de la ficha y del alta.  (061)
 */
import { useEffect, type ReactNode } from 'react'

import { Badge } from '../../components/ui/badge'
import { cn } from '../../lib/cn'

export function DialogoUsuario({
    titulo,
    onCerrar,
    children,
}: {
    titulo: string
    onCerrar: () => void
    children: ReactNode
}) {
    useEffect(() => {
        function tecla(e: KeyboardEvent) {
            if (e.key === 'Escape') onCerrar()
        }
        window.addEventListener('keydown', tecla)
        return () => window.removeEventListener('keydown', tecla)
    }, [onCerrar])

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4 sm:items-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-usuario"
            onClick={onCerrar}
        >
            <div
                className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between gap-3 border-b border-neutral-800 px-5 py-4">
                    <h2 id="titulo-usuario" className="text-base font-semibold text-neutral-100">
                        {titulo}
                    </h2>
                    <button
                        type="button"
                        onClick={onCerrar}
                        className="text-sm text-neutral-400 hover:text-neutral-200"
                    >
                        Cerrar
                    </button>
                </header>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
            </div>
        </div>
    )
}

export interface UsuarioDir {
    id: number
    email: string
    nombre: string
    status: 'active' | 'revoked'
    soloLectura: boolean
    tieneClave: boolean
    accountIds: number[]
}

export interface NegocioDir {
    accountId: number
    name: string
    estadoAlta: string
}

export function estadoDe(u: UsuarioDir): {
    texto: string
    tone: 'neutral' | 'warning' | 'danger' | 'info'
} {
    if (u.status === 'revoked') return { texto: 'Deshabilitado', tone: 'danger' }
    if (!u.tieneClave) return { texto: 'Invitado', tone: 'info' }
    return { texto: 'Activo', tone: 'neutral' }
}

export function cartelNombre(u: Pick<UsuarioDir, 'nombre' | 'email'>): string {
    const n = u.nombre.trim()
    return n.length > 0 ? n : u.email.split('@')[0] || u.email
}

export function SelectorAcceso({
    soloLectura,
    onChange,
}: {
    soloLectura: boolean
    onChange: (v: boolean) => void
}) {
    return (
        <div className="grid gap-2 sm:grid-cols-2">
            <button
                type="button"
                onClick={() => onChange(false)}
                className={cn(
                    'rounded-lg border px-4 py-3 text-left',
                    !soloLectura
                        ? 'border-emerald-700 bg-emerald-950/40'
                        : 'border-neutral-800 hover:border-neutral-600',
                )}
            >
                <p className="text-sm font-medium text-neutral-100">Puede escribir</p>
                <p className="mt-1 text-xs text-neutral-500">
                    Cambia citas, catálogo y plantillas de sus negocios.
                </p>
            </button>
            <button
                type="button"
                onClick={() => onChange(true)}
                className={cn(
                    'rounded-lg border px-4 py-3 text-left',
                    soloLectura
                        ? 'border-amber-700 bg-amber-950/40'
                        : 'border-neutral-800 hover:border-neutral-600',
                )}
            >
                <p className="text-sm font-medium text-neutral-100">Solo lectura</p>
                <p className="mt-1 text-xs text-neutral-500">
                    Ve el panel. No cambia nada.
                </p>
            </button>
        </div>
    )
}

export function RejillaNegocios({
    negocios,
    elegidos,
    onToggle,
}: {
    negocios: NegocioDir[]
    elegidos: number[]
    onToggle: (id: number) => void
}) {
    return (
        <div className="grid gap-2 sm:grid-cols-2">
            {negocios.map((n) => {
                const on = elegidos.includes(n.accountId)
                return (
                    <button
                        key={n.accountId}
                        type="button"
                        onClick={() => onToggle(n.accountId)}
                        className={cn(
                            'flex min-h-16 items-start justify-between gap-2 rounded-lg border px-3 py-3 text-left',
                            on
                                ? 'border-sky-700 bg-sky-950/30'
                                : 'border-neutral-800 hover:border-neutral-600',
                        )}
                    >
                        <span>
                            <span className="block text-sm font-medium text-neutral-100">
                                {n.name}
                            </span>
                            {n.estadoAlta === 'demo' && (
                                <span className="mt-0.5 block text-xs text-neutral-500">demo</span>
                            )}
                        </span>
                        {on && <Badge tone="info">Conectado</Badge>}
                    </button>
                )
            })}
        </div>
    )
}
